import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import API from "../lib/api";
import { Upload, ArrowRight, FileText, Loader2, RefreshCw, Trash2, AlertTriangle } from "lucide-react";

const DOC_TYPES = [
  // ── Banking / Regulatory
  { value: "regulatory", label: "Regulatory Guidelines" },
  { value: "policy", label: "Internal Policy" },
  { value: "sop", label: "SOP / Process" },
  { value: "rcm", label: "Risk & Control Matrix" },
  { value: "historical", label: "Historical Audit Report" },
  { value: "checklist", label: "Audit Checklist" },
  // ── Platform / E-Commerce
  { value: "fssai", label: "FSSAI Regulation / License" },
  { value: "gst", label: "GST Policy / Returns" },
  { value: "dpdp", label: "DPDP / Privacy Framework" },
  { value: "partner_compliance", label: "Partner Compliance Policy" },
  { value: "platform_sop", label: "Platform SOP" },
  { value: "incident_policy", label: "Incident Management Policy" },
  // ── Insurance (OJK/FSA / Life Underwriting)
  { value: "insurance_policy", label: "Insurance Policy Formulation" },
  { value: "underwriting_guidelines", label: "Life Underwriting Guidelines" },
  { value: "medical_report", label: "Medical Examiner Report (MER)" },
  { value: "financial_questionnaire", label: "Financial Questionnaire" },
  { value: "kyc_aml_form", label: "KYC / AML / PPATK Forms" },
  { value: "claims_sop", label: "Claims Processing SOP" },
  { value: "actuarial_report", label: "Actuarial Valuation Report" },
  { value: "ojk_rbc_report", label: "OJK RBC / Solvency Report" },
  { value: "reinsurance_treaty", label: "Reinsurance Treaty / Recoveries" },
  { value: "takaful_declaration", label: "Takaful / Sharia Compliance" },
  { value: "pii_data_policy", label: "POJK Data Privacy Policy" },
];

export default function Phase2Documents() {
  const navigate = useNavigate();
  const { auditRun, advancePhase, documents: contextDocs, fetchDocuments, dataVersion } = useAudit();
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("regulatory");

  const fetchDocs = useCallback(async () => {
    if (!auditRun?.id) return;
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/documents`);
      setDocuments(data.filter(d => d.doc_type !== "artifact"));
    } catch (e) { /* ignore */ }
  }, [auditRun?.id]);

  // Initial fetch and refresh when context dataVersion changes (from chat actions)
  useEffect(() => { fetchDocs(); }, [fetchDocs, dataVersion]);

  // Also sync from context if available
  useEffect(() => {
    if (contextDocs && contextDocs.length > 0) {
      setDocuments(contextDocs.filter(d => d.doc_type !== "artifact"));
    }
  }, [contextDocs]);

  // Polling for status updates
  useEffect(() => {
    const hasProcessing = documents.some(d => !["ready", "uploaded"].includes(d.status));
    if (!hasProcessing) return;
    const interval = setInterval(fetchDocs, 3000);
    return () => clearInterval(interval);
  }, [documents, fetchDocs]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !auditRun?.id) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", docType);
      try {
        await API.post(`/audit-runs/${auditRun.id}/documents`, formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        toast.success(`Uploaded: ${file.name}`);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.message) {
          // Regulatory validation rejection — show the specific reason
          toast.error(detail.message, { duration: 8000 });
        } else if (typeof detail === "string") {
          toast.error(detail, { duration: 6000 });
        } else {
          toast.error(`Upload failed: ${file.name}`);
        }
      }
    }
    setUploading(false);
    await fetchDocs();
    e.target.value = "";
  };

  const handleReclassify = async (docId, classification) => {
    try {
      await API.put(`/audit-runs/${auditRun.id}/documents/${docId}/classify`, { classification });
      await fetchDocs();
    } catch (e) { toast.error("Failed to reclassify"); }
  };

  const handleDelete = async (docId, filename) => {
    try {
      await API.delete(`/audit-runs/${auditRun.id}/documents/${docId}`);
      toast.success(`Deleted: ${filename}`);
      await fetchDocs();
    } catch (e) {
      toast.error("Failed to delete document");
    }
  };

  // Check if document appears to be a blocked/error page
  const isBadDocument = (doc) => {
    const badIndicators = ['blocked', 'just a moment', 'cloudflare', 'access denied', 'error', '403', '404'];
    const filename = (doc.filename || '').toLowerCase();
    const classification = (doc.classification || '').toLowerCase();
    return badIndicators.some(ind => filename.includes(ind) || classification.includes(ind)) ||
      (doc.file_size && doc.file_size < 1000); // Very small files are likely error pages
  };

  const allReady = documents.length > 0 && documents.every(d => d.status === "ready");

  const handleContinue = async () => {
    await advancePhase(3);
    navigate("/phase/3");
    toast.success("Proceeding to Control Mapping");
  };

  const statusColors = {
    uploaded: "bg-slate-500",
    ingesting: "bg-amber-500 animate-pulse",
    parsing: "bg-blue-500 animate-pulse",
    extracting: "bg-violet-500 animate-pulse",
    ready: "bg-emerald-500"
  };

  return (
    <div className="space-y-6" data-testid="phase-2">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Phase 2: Document Ingestion</h2>
          <p className="text-sm text-muted-foreground mt-1">Upload regulatory guidelines, compliance frameworks, SOPs, and platform policies. Supports PDF, DOCX, XLSX, CSV, TXT.</p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={fetchDocs} data-testid="refresh-docs-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={handleContinue} disabled={documents.length === 0} data-testid="continue-to-controls-top-btn">
            <ArrowRight className="w-4 h-4 mr-2" /> Continue to Control Mapping
          </Button>
        </div>
      </div>

      {/* Upload Area */}
      <Card>
        <CardHeader><CardTitle className="text-base">Upload Documents</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Document Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="mt-1" data-testid="select-doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="flex items-center justify-center gap-2 h-9 px-4 border border-dashed border-border rounded-md cursor-pointer hover:bg-accent/50 transition-colors text-sm">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? "Uploading..." : "Choose files"}
                <input type="file" multiple onChange={handleUpload} className="hidden"
                  accept=".pdf,.txt,.md,.csv,.xlsx,.xls,.docx,.doc"
                  data-testid="file-upload-input" disabled={!auditRun || uploading} />
              </label>
              <p className="text-xs text-muted-foreground mt-1 text-center">PDF &middot; DOCX &middot; XLSX &middot; CSV &middot; TXT</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      {documents.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Uploaded Documents ({documents.length})</span>
              {allReady && <Badge variant="outline" className="text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800">All Ready</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Classification</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Size</TableHead>
                  <TableHead className="w-[60px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map(doc => {
                  const isBad = isBadDocument(doc);
                  return (
                    <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`} className={isBad ? 'bg-red-500/10' : ''}>
                      <TableCell className="font-mono text-xs flex items-center gap-2">
                        {isBad ? (
                          <AlertTriangle className="w-3 h-3 text-red-500 shrink-0" title="This may be a blocked/error page" />
                        ) : (
                          <FileText className="w-3 h-3 text-muted-foreground shrink-0" />
                        )}
                        <span className={`truncate max-w-[200px] ${isBad ? 'text-red-500' : ''}`}>{doc.filename}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs font-mono">{doc.doc_type}</Badge>
                      </TableCell>
                      <TableCell>
                        <Input
                          defaultValue={doc.classification}
                          onBlur={e => e.target.value !== doc.classification && handleReclassify(doc.id, e.target.value)}
                          className="h-7 text-xs"
                          placeholder="Classification..."
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`status-dot ${statusColors[doc.status] || 'bg-slate-400'}`} />
                          <span className="text-xs font-mono capitalize">{doc.status}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        {doc.file_size ? `${(doc.file_size / 1024).toFixed(1)}KB` : '-'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDelete(doc.id, doc.filename)}
                          title="Delete document"
                          data-testid={`delete-doc-${doc.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      {documents.length === 0 && (
        <Card>
          <CardContent className="min-h-[280px] flex flex-col items-center justify-center text-center">
            <Upload className="w-9 h-9 text-blue-500/70 mb-3" />
            <p className="text-sm font-medium">No uploaded documents yet.</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-md">
              Upload regulatory guidelines, SOPs, and policy files to unlock control extraction and keep the audit flow moving.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button onClick={handleContinue} disabled={documents.length === 0} data-testid="continue-to-controls-btn">
          <ArrowRight className="w-4 h-4 mr-2" /> Continue to Control Mapping
        </Button>
      </div>
    </div>
  );
}
